import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { CheckCircle2, Video, Building } from "lucide-react";
import { api, errMsg, FIELD, AREA, fmtDate, fmtTime, fmtMoney } from "@/lib/api";
import { SERVICE_OPTIONS } from "@/lib/site";
import { PageHero } from "@/components/common/Reveal";
import { Field } from "@/components/common/Field";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const today = new Date(); today.setHours(0, 0, 0, 0);

const SlotPicker = ({ date, value, onChange }) => {
  const [slots, setSlots] = useState([]);
  useEffect(() => {
    if (!date) return;
    api.get("/appointments/availability", { params: { date } }).then((r) => setSlots(r.data.slots));
  }, [date]);
  if (!date) return <p className="text-sm text-white/50">Select a date to view available times.</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="time-slots">
      {slots.map((s) => (
        <button type="button" key={s.time} disabled={!s.available} onClick={() => onChange(s.time)} data-testid={`time-slot-${s.time.replace(":", "")}`}
          className={`h-12 rounded-xl border text-sm transition-colors duration-300 ${value === s.time ? "bg-[#D4AF37] text-black border-[#D4AF37]" : s.available ? "border-white/20 hover:border-[#D4AF37] text-white" : "border-white/5 text-white/20 line-through cursor-not-allowed"}`}>
          {fmtTime(s.time)}
        </button>
      ))}
    </div>
  );
};

const Success = ({ b }) => (
  <div className="lux-card p-12 text-center max-w-2xl mx-auto" data-testid="booking-success">
    <CheckCircle2 className="w-14 h-14 text-[#D4AF37] mx-auto" />
    <h2 className="mt-6 text-4xl font-medium">Your consultation is requested.</h2>
    <p className="mt-4 text-white/70">{b.service} · {fmtDate(b.date)} at {fmtTime(b.time)} ({b.meeting_type})</p>
    <p className="mt-2 text-sm text-white/50">We'll confirm your appointment shortly by email.</p>
    <Link to="/portal" className="btn-outline-gold mt-8" data-testid="booking-success-portal-link">View in Client Portal</Link>
    <p className="mt-3 text-xs text-white/45">Sign in with Google using the same email you booked with.</p>
  </div>
);

export default function Book() {
  const [params] = useSearchParams();
  const [day, setDay] = useState();
  const [f, setF] = useState({ full_name: "", email: "", phone: "", service: params.get("service") || "", time: "", meeting_type: "virtual", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [fees, setFees] = useState({});
  useEffect(() => { api.get("/service-fees").then((r) => setFees(r.data)); }, []);
  const fee = fees[f.service] || 0;
  const date = day ? format(day, "yyyy-MM-dd") : "";
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.service || !date || !f.time) return toast.error("Please choose a service, date and time.");
    setBusy(true);
    try {
      const { data } = await api.post("/appointments", { ...f, date, origin_url: window.location.origin });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="book-page">
      <PageHero testid="book-hero" eyebrow="Appointment Booking" title={<>Book your <span className="italic gold-text">strategy session.</span></>}
        sub="Choose a date and time that works for you. Consultations are available Monday through Saturday, 10 AM – 6 PM, virtually or in person." />
      <section className="bg-black py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {done ? <Success b={done} /> : (
            <form onSubmit={submit} className="grid lg:grid-cols-12 gap-8" data-testid="booking-form">
              <div className="lg:col-span-5 lux-card p-6 md:p-8 space-y-8">
                <div>
                  <p className="eyebrow mb-4">1 · Choose a date</p>
                  <Calendar mode="single" selected={day} onSelect={(d) => { setDay(d); setF({ ...f, time: "" }); }}
                    disabled={(d) => d < today || d.getDay() === 0} className="rounded-xl border border-white/10 bg-black/40 w-full sm:w-fit p-2 sm:p-3" data-testid="booking-calendar"
                    classNames={{
                      months: "w-full", month: "w-full space-y-4", table: "w-full border-collapse",
                      head_row: "flex w-full", head_cell: "flex-1 sm:w-10 sm:flex-none text-white/45 font-normal text-xs",
                      row: "flex w-full mt-1", cell: "flex-1 sm:w-10 sm:flex-none p-0.5 text-center text-sm relative focus-within:z-20",
                      day: "h-11 sm:h-10 w-full rounded-lg font-normal hover:bg-white/10 transition-colors aria-selected:opacity-100",
                      nav_button: "h-9 w-9 flex items-center justify-center rounded-lg border border-white/15 hover:border-[#D4AF37] transition-colors",
                    }} />
                </div>
                <div>
                  <p className="eyebrow mb-4">2 · Choose a time {date && <span className="text-white/50 normal-case tracking-normal">— {fmtDate(date)}</span>}</p>
                  <SlotPicker date={date} value={f.time} onChange={set("time")} />
                </div>
              </div>
              <div className="lg:col-span-7 lux-card p-6 md:p-10 grid sm:grid-cols-2 gap-6 content-start">
                <p className="eyebrow sm:col-span-2">3 · Your details</p>
                <Field label="Service" required className="sm:col-span-2">
                  <Select value={f.service} onValueChange={set("service")}>
                    <SelectTrigger className={FIELD} data-testid="booking-service-select"><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent>{SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}{fees[s] ? ` — ${fmtMoney(fees[s])}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Full Name" required><Input className={FIELD} required value={f.full_name} onChange={set("full_name")} data-testid="booking-name-input" /></Field>
                <Field label="Email" required><Input className={FIELD} type="email" required value={f.email} onChange={set("email")} data-testid="booking-email-input" /></Field>
                <Field label="Phone" required><Input className={FIELD} required value={f.phone} onChange={set("phone")} data-testid="booking-phone-input" /></Field>
                <Field label="Meeting Type">
                  <div className="grid grid-cols-2 gap-3">
                    {[["virtual", Video, "Virtual"], ["in-person", Building, "In Person"]].map(([v, Icon, l]) => (
                      <button type="button" key={v} onClick={() => setF({ ...f, meeting_type: v })} data-testid={`booking-type-${v}`}
                        className={`h-12 rounded-xl border flex items-center justify-center gap-2 text-sm transition-colors duration-300 ${f.meeting_type === v ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/20 text-white/70"}`}>
                        <Icon className="w-4 h-4" />{l}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Notes" className="sm:col-span-2"><Textarea className={AREA} placeholder="Anything we should know before we meet?" value={f.notes} onChange={set("notes")} data-testid="booking-notes-input" /></Field>
                <div className="sm:col-span-2">
                  <button className="btn-gold" disabled={busy} data-testid="booking-submit-button">{busy ? "Please wait…" : fee ? `Continue to Payment · ${fmtMoney(fee)}` : "Request Consultation"}</button>
                  {fee > 0 && <p className="mt-3 text-xs text-white/45" data-testid="booking-fee-note">A {fmtMoney(fee)} consultation fee is paid securely through Stripe. Your time slot is held while you check out.</p>}
                </div>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
